import { useEffect } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

// Soft UI Dashboard React examples
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";

// Soft UI Dashboard React context
import { useSoftUIController, setMiniSidenav } from "context";
import useStore from "store/hook";
import SidenavSmall from "./SidenavSmall";
import DevModeBanner from "./DevModeBanner";
import smallLogo from "assets/images/smallLogo.png";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useSoftUIController();
  const { miniSidenav, transparentSidenav } = controller;
  const location = useLocation();
  let { pathname } = location;
  pathname = location.pathname.split("/")[1];
  const { setDialogue, store } = useStore();
  const { isCollapse } = store?.app?.dialogs?.collapseSidenav || {};

  const routeMapping = {
    dashboard: "dashboard",
    users: "users",
    "user-details": "users",
    "reported-users": "reported users",
    universities: "universities",
    "add-school": "schools",
    "school-details": "schools",
    "edit-school": "schools",
    schools: "schools",
    cities: "cities",
    "city-details": "cities",
    "add-city": "cities",
    "edit-city": "cities",
    settings: "settings",
  };

  const collapseKey = Object.keys(routeMapping).find((route) => pathname.startsWith(route));

  const collapseName = collapseKey ? routeMapping[collapseKey] : null;

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch, location]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, activeIcon, title, noCollapse, key, route, href }) => {
    let returnValue;
    const isActive = key === collapseName;
    const resolvedIcon = isActive && activeIcon ? activeIcon : icon;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
          onClick={() =>
            setDialogue({
              type: "toggleSidebar",
              isOpen: false,
            })
          }
        >
          <SidenavCollapse
            color={color}
            name={name}
            icon={resolvedIcon}
            active={collapseName === name}
            noCollapse={noCollapse}
          />
        </Link>
      ) : (
        <NavLink
          to={route}
          key={href}
          onClick={() =>
            setDialogue({
              type: "toggleSidebar",
              isOpen: false,
            })
          }
        >
          <SidenavCollapse
            color={color}
            key={key}
            name={name}
            icon={resolvedIcon}
            active={isActive}
            noCollapse={noCollapse}
          />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <SoftTypography
          key={href}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          opacity={0.6}
        >
          {title}
        </SoftTypography>
      );
    } else if (type === "divider") {
      returnValue = <Divider key={href} />;
    }

    return returnValue;
  });

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const collapserenderRoutes = routes.map(
    ({ type, name, icon, activeIcon, title, noCollapse, key, route, href }) => {
      let returnValue;
      const isActive = key === collapseName;
      const resolvedIcon = isActive && activeIcon ? activeIcon : icon;

      if (type === "collapse") {
        returnValue = href ? (
          <Link
            href={href}
            key={href}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavSmall
              color={color}
              name={name}
              icon={resolvedIcon}
              active={isActive}
              noCollapse={noCollapse}
            />
          </Link>
        ) : (
          <NavLink to={route} key={href}>
            <SidenavSmall
              color={color}
              key={key}
              name={name}
              icon={resolvedIcon}
              active={isActive}
              noCollapse={noCollapse}
            />
          </NavLink>
        );
      }

      return returnValue;
    }
  );

  return (
    <SidenavRoot {...rest} variant="permanent" ownerState={{ transparentSidenav, miniSidenav }}>
      <DevModeBanner />
      <SoftBox pt={3} pb={14} px={4} textAlign="center">
        <NavLink
          onClick={() =>
            setDialogue({
              type: "toggleSidebar",
              isOpen: false,
            })
          }
        >
          <SoftBox
            display={{ xs: "block", xl: "none" }}
            position="absolute"
            top={0}
            right={0}
            p={1.625}
            onClick={() => {
              setDialogue({
                type: "toggleSidebar",
                isOpen: false,
              });
            }}
            sx={{ cursor: "pointer", zIndex: 2000 }}
          >
            <SoftTypography variant="h6" color="secondary">
              <Icon sx={{ fontWeight: "bold" }}>close</Icon>
            </SoftTypography>
          </SoftBox>
        </NavLink>

        <SoftBox
          component={NavLink}
          to="/dashboard"
          display="flex"
          alignItems="center"
          width="100%"
          justifyContent="center"
        >
          {brand && isCollapse ? (
            <SoftBox component="img" src={smallLogo} alt="Soft UI Logo" width="3rem" />
          ) : (
            <SoftBox component="img" src={brand} alt="Soft UI Logo" width="10rem" />
          )}
        </SoftBox>
      </SoftBox>

      {isCollapse ? (
        <List sx={{ display: "flex", flexDirection: "column", gap: 2, px: 1 }}>
          {collapserenderRoutes}
        </List>
      ) : (
        <List sx={{ display: "flex", flexDirection: "column", gap: 2 }}>{renderRoutes}</List>
      )}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
